/**
 * v6.33.0 — Sentry error tracking for the React frontend.
 *
 * Configuration is fully env-driven. If REACT_APP_SENTRY_DSN is unset
 * (the default — operator opts in by populating frontend/.env), this
 * module is a no-op: SDK never initializes, no events sent, zero
 * runtime overhead beyond a single environment-variable read.
 *
 * MUST be the very first import in src/index.js so SDK handlers are
 * registered before any other React or library code runs (Sentry's
 * React SDK docs explicitly require this — late init misses errors
 * that occur before init completes).
 *
 * FERPA / student-data protection:
 *   • sendDefaultPii=false — SDK never includes user IPs, cookies,
 *     headers in events unless we explicitly opt in (we don't).
 *   • beforeSend hook strips student-related field names from
 *     request bodies + extra context client-side before transmission.
 *   • tracesSampleRate=0 — performance traces disabled on the
 *     frontend to minimize bundle/network overhead. Backend tracing
 *     covers the API-side latency picture.
 *   • No ReplayIntegration / BrowserTracing — kept the bundle lean.
 *
 * The companion <Sentry.ErrorBoundary> is mounted in src/index.js
 * around <App /> to catch React render-cycle errors with a friendly
 * shadcn-based fallback.
 */
import * as Sentry from "@sentry/react";

const STUDENT_PII_TOKENS = [
  "student", "pupil", "learner", "ssn", "dob",
  "birthdate", "parent_email", "guardian_email", "phone",
];

const isPiiKey = (k) => {
  const lower = String(k || "").toLowerCase();
  return STUDENT_PII_TOKENS.some((tok) => lower.includes(tok));
};

const scrubObject = (obj) => {
  if (!obj || typeof obj !== "object") return;
  for (const k of Object.keys(obj)) {
    if (isPiiKey(k)) {
      delete obj[k];
    }
  }
};

const dsn = process.env.REACT_APP_SENTRY_DSN || "";

if (dsn && dsn.trim().length > 0) {
  // SDK init only happens when operator has populated the env var.
  // This is the expected initial state immediately after the v6.33.0
  // ship — operator opts in by adding REACT_APP_SENTRY_DSN to
  // frontend/.env when their Sentry project is provisioned.
  Sentry.init({
    dsn,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.NODE_ENV ||
      "development",
    release: process.env.REACT_APP_RELEASE || undefined,

    // Minimal-overhead profile — error capture only, no perf/replay.
    sampleRate: 1.0,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    integrations: [],

    beforeSend(event) {
      // Belt-and-suspenders user scrub — SDK already omits these when
      // sendDefaultPii=false, but guard against a manual setUser leak.
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }

      // Request body scrub
      if (event.request && event.request.data && typeof event.request.data === "object") {
        scrubObject(event.request.data);
      }

      // Extra + tags scrub
      scrubObject(event.extra);
      scrubObject(event.tags);

      return event;
    },
  });

  // Mark the build for debugging convenience
  // eslint-disable-next-line no-console
  console.info(
    `[Sentry] initialized · env=${process.env.REACT_APP_SENTRY_ENVIRONMENT || "default"} · release=${process.env.REACT_APP_RELEASE || "unset"}`,
  );
}

export default Sentry;
